/**
 * Suggestion Button Component
 * Clickable button/chip for chat suggestions
 * Category-based styling with smooth animations
 * Following soft, modern design principles
 */

import React, { useCallback } from 'react';
import { motion } from 'motion/react';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import type { ChatSuggestion, SuggestionCategory } from '../types/chat.types';
import { getSuggestionAriaLabel } from '../utils/suggestionCopy';

interface SuggestionButtonProps {
  suggestion: ChatSuggestion;
  onClick: (suggestion: ChatSuggestion) => void;
  index?: number; // For stagger animation
  className?: string;
}

/**
 * Get category-based color classes
 */
const getCategoryColors = (category: SuggestionCategory) => {
  switch (category) {
    case 'platform':
      return {
        bg: 'bg-emerald-50 dark:bg-emerald-950/20',
        hover: 'hover:bg-emerald-100 dark:hover:bg-emerald-950/30',
        border: 'border-emerald-200 dark:border-emerald-800',
        text: 'text-emerald-700 dark:text-emerald-400',
        icon: 'text-emerald-600 dark:text-emerald-500',
      };
    case 'donation':
      return {
        bg: 'bg-amber-50 dark:bg-amber-950/20',
        hover: 'hover:bg-amber-100 dark:hover:bg-amber-950/30',
        border: 'border-amber-200 dark:border-amber-800',
        text: 'text-amber-700 dark:text-amber-400',
        icon: 'text-amber-600 dark:text-amber-500',
      };
    case 'islamic':
      return {
        bg: 'bg-green-50 dark:bg-green-950/20',
        hover: 'hover:bg-green-100 dark:hover:bg-green-950/30',
        border: 'border-green-200 dark:border-green-800',
        text: 'text-green-700 dark:text-green-400',
        icon: 'text-green-600 dark:text-green-500',
      };
    case 'campaign':
      return {
        bg: 'bg-blue-50 dark:bg-blue-950/20',
        hover: 'hover:bg-blue-100 dark:hover:bg-blue-950/30',
        border: 'border-blue-200 dark:border-blue-800',
        text: 'text-blue-700 dark:text-blue-400',
        icon: 'text-blue-600 dark:text-blue-500',
      };
    default:
      return {
        bg: 'bg-gray-50 dark:bg-gray-950/20',
        hover: 'hover:bg-gray-100 dark:hover:bg-gray-950/30',
        border: 'border-gray-200 dark:border-gray-800',
        text: 'text-gray-700 dark:text-gray-400',
        icon: 'text-gray-600 dark:text-gray-500',
      };
  }
};

/**
 * Suggestion button/chip component
 * Displays suggestion text with category-based styling
 */
/**
 * Suggestion button/chip component
 * Displays suggestion text with category-based styling
 * Handles click events with proper error boundaries
 */
export const SuggestionButton: React.FC<SuggestionButtonProps> = ({
  suggestion,
  onClick,
  index = 0,
  className,
}) => {
  const colors = getCategoryColors(suggestion.category);
  const hasActionUrl = Boolean(suggestion.actionUrl);

  /**
   * Handle button click with error handling
   */
  const handleClick = useCallback(() => {
    try {
      onClick(suggestion);
    } catch (error) {
      console.error('Error handling suggestion click:', error);
      // Error is logged but not thrown to prevent breaking the UI
    }
  }, [onClick, suggestion]);

  return (
    <motion.button
      type="button"
      onClick={handleClick}
      initial={{ opacity: 0, y: 8, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.3,
        delay: index * 0.05, // Stagger animation
        ease: [0.16, 1, 0.3, 1],
      }}
      whileHover={{ scale: 1.02, y: -1 }}
      whileTap={{ scale: 0.98 }}
      className={cn(
        'inline-flex items-center gap-2',
        'px-4 py-2.5 rounded-xl',
        'border',
        'transition-all duration-200',
        'focus:outline-hidden focus:ring-2 focus:ring-offset-1',
        'text-sm font-medium',
        'whitespace-normal',
        'text-left',
        colors.bg,
        colors.hover,
        colors.border,
        colors.text,
        className
      )}
      aria-label={getSuggestionAriaLabel(suggestion.text, suggestion.category)}
      title={hasActionUrl ? 'Click to ask about this topic and navigate to the relevant page' : 'Click to ask about this topic'}
    >
      <span className="flex-1">{suggestion.text}</span>
      {hasActionUrl ? (
        <ExternalLink className={cn('w-4 h-4 shrink-0', colors.icon)} />
      ) : (
        <ArrowRight className={cn('w-4 h-4 shrink-0', colors.icon)} />
      )}
    </motion.button>
  );
};

